import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

type Politeness = "polite" | "assertive";

export interface AnnounceOptions {
  /** "assertive" interrupts the user. Reserve it for errors and completions. */
  politeness?: Politeness;
  /** Repeats and throttling are tracked per channel, so unrelated streams don't cancel each other. */
  channel?: string;
  /** Minimum ms between spoken updates on this channel. Bursts collapse to the latest message. */
  throttleMs?: number;
  /** Re-announce even if the text is identical to the last one on this channel. */
  force?: boolean;
}

type Announce = (message: string, options?: AnnounceOptions) => void;

interface ChannelState {
  lastMessage: string;
  lastAnnouncedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const AnnouncerContext = createContext<Announce | undefined>(undefined);

/**
 * Owns the app's two live regions.
 *
 * Mount this outside the router: if a route change unmounts the live nodes, the
 * announcement that accompanies it is swallowed.
 */
export function AnnouncerProvider({ children }: { children: React.ReactNode }) {
  const politeRef = useRef<HTMLDivElement>(null);
  const assertiveRef = useRef<HTMLDivElement>(null);
  const channelsRef = useRef(new Map<string, ChannelState>());

  useEffect(() => {
    const channels = channelsRef.current;
    return () => {
      for (const channel of channels.values()) {
        if (channel.timer) clearTimeout(channel.timer);
      }
      channels.clear();
    };
  }, []);

  const write = useCallback(
    (message: string, politeness: Politeness, force: boolean) => {
      const node =
        politeness === "assertive" ? assertiveRef.current : politeRef.current;
      if (!node) return;
      // Screen readers speak on a *change* of text content, so re-announcing
      // identical text needs a zero-width space to force a diff.
      node.textContent =
        force && node.textContent === message ? `${message}\u200B` : message;
    },
    [],
  );

  // Writes go straight to the DOM rather than through state: the task log
  // polls every 200ms, and a state write there would re-render the whole app
  // five times a second.
  const announce = useCallback<Announce>(
    (message, options = {}) => {
      const {
        politeness = "polite",
        channel = "default",
        throttleMs = 0,
        force = false,
      } = options;

      const text = message.trim();
      if (!text) return;

      const channels = channelsRef.current;
      const state = channels.get(channel) ?? {
        lastMessage: "",
        lastAnnouncedAt: Number.NEGATIVE_INFINITY,
        timer: null,
      };
      channels.set(channel, state);

      if (!force && text === state.lastMessage) return;

      const flush = () => {
        state.timer = null;
        state.lastMessage = text;
        state.lastAnnouncedAt = Date.now();
        write(text, politeness, force);
      };

      const elapsed = Date.now() - state.lastAnnouncedAt;
      if (throttleMs <= 0 || elapsed >= throttleMs) {
        if (state.timer) clearTimeout(state.timer);
        flush();
        return;
      }

      // Inside the throttle window: replace any pending message so the latest
      // one wins, and speak it when the window closes.
      if (state.timer) clearTimeout(state.timer);
      state.timer = setTimeout(flush, throttleMs - elapsed);
    },
    [write],
  );

  return (
    <AnnouncerContext.Provider value={announce}>
      {children}
      <div
        ref={politeRef}
        role="status"
        aria-atomic="true"
        className="sr-only"
      />
      <div
        ref={assertiveRef}
        role="alert"
        aria-atomic="true"
        className="sr-only"
      />
    </AnnouncerContext.Provider>
  );
}

export function useAnnounce(): Announce {
  const context = useContext(AnnouncerContext);
  if (!context) {
    throw new Error("useAnnounce must be used within an AnnouncerProvider");
  }
  return context;
}
